import type {
  CheckboxMode,
  DateRestrict,
  FormFieldType,
  FormStatus,
  IFormField,
  IFormFieldOption,
  OptionValueFormat,
  PhoneFormat,
  RadioLayout,
} from "./types";

export const FORM_STATUS = {
  ACTIVE: "active",
  DRAFT: "draft",
  INACTIVE: "inactive",
} as const;

export const FORM_TYPE = {
  TEMPLATE: "template",
  CUSTOM: "custom",
} as const;

export const FORM_STATUS_OPTIONS: { value: FormStatus; label: string }[] = [
  { value: FORM_STATUS.ACTIVE, label: "Active" },
  { value: FORM_STATUS.DRAFT, label: "Draft" },
  { value: FORM_STATUS.INACTIVE, label: "Inactive" },
];

export const FORM_STATUS_FILTER_OPTIONS = [
  { value: "", label: "All Status" },
  ...FORM_STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
];

export const FORM_TYPE_FILTER_OPTIONS = [
  { value: "", label: "All Forms" },
  { value: FORM_TYPE.TEMPLATE, label: "Templates Only" },
  { value: FORM_TYPE.CUSTOM, label: "Custom Forms" },
];

export const FORM_FIELD_TYPE_OPTIONS: {
  value: FormFieldType;
  label: string;
  description: string;
}[] = [
  { value: "TEXT", label: "Text", description: "Single-line text input" },
  {
    value: "TEXTAREA",
    label: "Textarea",
    description: "Multi-line paragraph text",
  },
  {
    value: "NUMBER",
    label: "Number",
    description: "Numeric input with min/max",
  },
  { value: "EMAIL", label: "Email", description: "Email address field" },
  { value: "PHONE", label: "Phone", description: "Phone / mobile number" },
  { value: "DROPDOWN", label: "Dropdown", description: "Single select list" },
  {
    value: "MULTI_SELECT",
    label: "Multi Select",
    description: "Multiple selection list",
  },
  { value: "RADIO", label: "Radio", description: "Single choice radio group" },
  {
    value: "CHECKBOX",
    label: "Checkbox",
    description: "Boolean yes/no or multi-option group",
  },
  { value: "SWITCH", label: "Switch", description: "Yes / No toggle" },
  { value: "DATE", label: "Date", description: "Date picker" },
  { value: "TIME", label: "Time", description: "Time picker" },
  {
    value: "FILE",
    label: "File Upload",
    description: "Document or image upload",
  },
];

export const FORM_FIELD_TYPE_META: Record<
  FormFieldType,
  { label: string; badgeColor: string }
> = {
  TEXT: { label: "Text", badgeColor: "light-primary" },
  TEXTAREA: { label: "Textarea", badgeColor: "light-primary" },
  NUMBER: { label: "Number", badgeColor: "light-warning" },
  EMAIL: { label: "Email", badgeColor: "light-info" },
  PHONE: { label: "Phone", badgeColor: "light-info" },
  DROPDOWN: { label: "Dropdown", badgeColor: "light-info" },
  MULTI_SELECT: { label: "Multi Select", badgeColor: "light-success" },
  RADIO: { label: "Radio", badgeColor: "light-primary" },
  CHECKBOX: { label: "Checkbox", badgeColor: "light-success" },
  SWITCH: { label: "Switch", badgeColor: "light-secondary" },
  DATE: { label: "Date", badgeColor: "light-warning" },
  TIME: { label: "Time", badgeColor: "light-warning" },
  FILE: { label: "File", badgeColor: "light-secondary" },
};

export const BUILDER_FIELD_PALETTE = FORM_FIELD_TYPE_OPTIONS;

export const CHECKBOX_MODE_OPTIONS: {
  value: CheckboxMode;
  label: string;
  description: string;
}[] = [
  {
    value: "boolean",
    label: "Boolean (single)",
    description: "Returns true / false — terms, opt-in, enable feature",
  },
  {
    value: "multiple",
    label: "Multiple (group)",
    description: "Returns selected values as string[] or object[]",
  },
];

export const OPTION_VALUE_FORMAT_OPTIONS: {
  value: OptionValueFormat;
  label: string;
  description: string;
}[] = [
  {
    value: "string",
    label: "String array",
    description: '["React", "Node.js"]',
  },
  {
    value: "object",
    label: "Object array",
    description: "[{ id, name, value }]",
  },
];

/** Alias for checkbox settings */
export const CHECKBOX_VALUE_FORMAT_OPTIONS = OPTION_VALUE_FORMAT_OPTIONS;

export const PHONE_FORMAT_OPTIONS: {
  value: PhoneFormat;
  label: string;
  description: string;
}[] = [
  { value: "any", label: "Any text", description: "No strict format" },
  {
    value: "digits",
    label: "Digits only",
    description: "7–15 digits (spaces/dashes allowed while typing)",
  },
  {
    value: "intl",
    label: "International",
    description: "Optional + then 8–15 digits",
  },
];

export const DATE_RESTRICT_OPTIONS: {
  value: DateRestrict;
  label: string;
}[] = [
  { value: "any", label: "Any date" },
  { value: "past", label: "Past only (≤ today)" },
  { value: "future", label: "Future only (≥ today)" },
  { value: "range", label: "Custom min / max" },
];

export const RADIO_LAYOUT_OPTIONS: {
  value: RadioLayout;
  label: string;
}[] = [
  { value: "vertical", label: "Vertical" },
  { value: "horizontal", label: "Horizontal" },
];

export const FILE_ACCEPT_PRESETS: {
  value: string;
  label: string;
}[] = [
  { value: "", label: "Any file" },
  { value: "image/*", label: "Images only" },
  {
    value: ".pdf,application/pdf",
    label: "PDF only",
  },
  {
    value: "image/*,.pdf,application/pdf,.doc,.docx",
    label: "Images + documents",
  },
];

/** Field types that always require option lists */
export const OPTION_FIELD_TYPES: FormFieldType[] = [
  "DROPDOWN",
  "MULTI_SELECT",
  "RADIO",
];

export const getCheckboxMode = (field: IFormField): CheckboxMode =>
  field.type === "CHECKBOX" ? (field.checkboxMode ?? "multiple") : "multiple";

export const getOptionValueFormat = (field: IFormField): OptionValueFormat => {
  if (field.type === "CHECKBOX") {
    return field.checkboxValueFormat ?? "string";
  }
  return "string";
};

export const getCheckboxValueFormat = getOptionValueFormat;

export const getPhoneFormat = (field: IFormField): PhoneFormat =>
  field.phoneFormat ?? "any";

export const getDateRestrict = (field: IFormField): DateRestrict =>
  field.dateRestrict ?? "any";

export const getRadioLayout = (field: IFormField): RadioLayout =>
  field.radioLayout ?? "vertical";

/** Whether this field uses an options editor */
export const fieldNeedsOptions = (field: IFormField): boolean => {
  if (OPTION_FIELD_TYPES.includes(field.type)) return true;
  if (field.type === "CHECKBOX") return getCheckboxMode(field) === "multiple";
  return false;
};

export const TEXT_LENGTH_FIELD_TYPES: FormFieldType[] = [
  "TEXT",
  "TEXTAREA",
  "EMAIL",
  "PHONE",
];

export const NUMBER_RANGE_FIELD_TYPES: FormFieldType[] = ["NUMBER"];

export const DEFAULT_FIELD_OPTIONS: IFormFieldOption[] = [
  { id: "opt_default_1", label: "", value: "" },
  { id: "opt_default_2", label: "", value: "" },
];

export const createDefaultOptions = (): IFormFieldOption[] =>
  DEFAULT_FIELD_OPTIONS.map((opt) => ({
    ...opt,
    id: `${opt.id}_${Date.now()}`,
  }));

export const createOption = (
  label: string,
  value?: string
): IFormFieldOption => {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return {
    id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    label,
    value: value ?? (slug || `option_${Date.now()}`),
  };
};

/** Defaults applied when adding a field in the builder */
export const getFieldTypeDefaults = (
  type: FormFieldType
): Partial<IFormField> => {
  switch (type) {
    case "CHECKBOX":
      return {
        checkboxMode: "multiple",
        checkboxValueFormat: "string",
        options: createDefaultOptions(),
      };
    case "DROPDOWN":
      return {
        options: createDefaultOptions(),
        searchable: true,
        clearable: true,
      };
    case "MULTI_SELECT":
      return {
        options: createDefaultOptions(),
        searchable: true,
        validation: { required: false, minSelections: 0 },
      };
    case "RADIO":
      return {
        options: createDefaultOptions(),
        radioLayout: "vertical",
      };
    case "TEXTAREA":
      return { rows: 3 };
    case "NUMBER":
      return { allowDecimal: true, validation: { required: false, step: 1 } };
    case "PHONE":
      return { phoneFormat: "digits" };
    case "DATE":
      return { dateRestrict: "any" };
    case "FILE":
      return {
        fileMultiple: false,
        fileMaxSizeMb: 5,
        fileAccept: "",
      };
    case "SWITCH":
      return { defaultChecked: false };
    default:
      return {};
  }
};

export const optionToObjectValue = (opt: IFormFieldOption) => ({
  id: opt.id ?? opt.value,
  name: opt.label,
  value: opt.value,
});

export const STATUS_BADGE_COLOR: Record<FormStatus, string> = {
  active: "light-success",
  draft: "light-warning",
  inactive: "light-secondary",
};

export const CONDITIONAL_OPERATOR_OPTIONS = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Not Equals" },
  { value: "contains", label: "Contains" },
] as const;

export const CONDITIONAL_ACTION_OPTIONS = [
  { value: "show_field", label: "Show Field" },
  { value: "hide_field", label: "Hide Field" },
  { value: "show_step", label: "Show Step" },
  { value: "hide_step", label: "Hide Step" },
] as const;

export const todayIsoDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const resolveDateBounds = (
  field: IFormField
): { min?: string; max?: string } => {
  const restrict = getDateRestrict(field);
  const today = todayIsoDate();
  if (restrict === "past") return { max: today };
  if (restrict === "future") return { min: today };
  if (restrict === "range") {
    return {
      min: field.validation?.minDate,
      max: field.validation?.maxDate,
    };
  }
  return {
    min: field.validation?.minDate,
    max: field.validation?.maxDate,
  };
};
